import type {
  DeviceNotice,
  NotificationEmail,
  NotificationRun,
  NotificationSettings,
  NotificationSettingsList,
  NotificationSnooze,
  PenActivityMonth,
  SettingsPatch,
  SnoozeInput,
} from '../domain/notification';
import {
  deviceNoticeSchema,
  notificationEmailSchema,
  notificationRunSchema,
  notificationSettingsSchema,
  notificationSnoozeSchema,
  penActivitySchema,
} from '../domain/validation';
import {
  ApiError,
  ConflictError,
  ForbiddenError,
  NotFoundError,
  RunTooSoonError,
  ValidationError,
} from './errors';
import type { NotificationsApi } from './notifications-api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

// gRPC status codes leatherback returns through Envoy.
const INVALID_ARGUMENT = 3;
const NOT_FOUND = 5;
const PERMISSION_DENIED = 7;
const FAILED_PRECONDITION = 9;
const ABORTED = 10;
const UNIMPLEMENTED = 12;

export class LiveNotificationsApi implements NotificationsApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.message === 'string'
          ? body.message
          : `Request failed with status ${response.status}`;
      switch (body.code) {
        case NOT_FOUND:
          throw new NotFoundError(message);
        case ABORTED:
          throw new ConflictError(message);
        case PERMISSION_DENIED:
          throw new ForbiddenError(message);
        case INVALID_ARGUMENT:
          throw new ValidationError(message);
        case FAILED_PRECONDITION:
          if (message.startsWith('LAST_RUN_RECENT')) {
            throw new RunTooSoonError(message.replace(/^[A-Z_]+:\s*/, ''));
          }
          throw new ValidationError(message.replace(/^[A-Z_]+:\s*/, ''));
        case UNIMPLEMENTED:
          throw new ApiError('Camera summaries aren’t set up on this server.', 'UNIMPLEMENTED');
      }
      if (response.status === 404) throw new NotFoundError(message);
      throw new ApiError(message, 'INTERNAL');
    }
    return body;
  }

  async listSettings(): Promise<NotificationSettingsList> {
    const raw = await this.fetchJSON('/v1/notificationSettings');
    const overrides = Array.isArray(raw.overrides) ? raw.overrides : [];
    return {
      defaults: notificationSettingsSchema.parse(raw.defaults ?? {}),
      overrides: overrides.map((row) => notificationSettingsSchema.parse(row)),
    };
  }

  async updateSettings(patch: SettingsPatch): Promise<NotificationSettings> {
    const params = new URLSearchParams();
    if (patch.clear.length > 0) params.set('clearMask', patch.clear.join(','));
    const query = params.toString();
    const settings = {
      type: 'DEVICE_SUMMARY',
      ...(patch.customerId ? { customerId: patch.customerId } : {}),
      version: String(patch.version),
      ...patch.values,
    };
    const raw = await this.fetchJSON(`/v1/notificationSettings${query ? `?${query}` : ''}`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
    return notificationSettingsSchema.parse(raw);
  }

  async runDeviceSummary(force = false): Promise<NotificationRun> {
    const raw = await this.fetchJSON('/v1/notificationRuns:deviceSummary', {
      method: 'POST',
      body: JSON.stringify(force ? { force: true } : {}),
    });
    return notificationRunSchema.parse(raw);
  }

  async listRuns(): Promise<NotificationRun[]> {
    const raw = await this.fetchJSON('/v1/notificationRuns?pageSize=20');
    const rows = Array.isArray(raw.runs) ? raw.runs : [];
    return rows.map((row) => notificationRunSchema.parse(row));
  }

  async listNotices(runId = 'latest'): Promise<{ run?: NotificationRun; notices: DeviceNotice[] }> {
    const raw = await this.fetchJSON(
      `/v1/notificationRuns/${encodeURIComponent(runId)}/deviceNotices`,
    );
    const rows = Array.isArray(raw.notices) ? raw.notices : [];
    return {
      ...(raw.run ? { run: notificationRunSchema.parse(raw.run) } : {}),
      notices: rows.map((row) => deviceNoticeSchema.parse(row)),
    };
  }

  async listEmails(runId: string): Promise<NotificationEmail[]> {
    const raw = await this.fetchJSON(`/v1/notificationRuns/${encodeURIComponent(runId)}/emails`);
    const rows = Array.isArray(raw.emails) ? raw.emails : [];
    return rows.map((row) => notificationEmailSchema.parse(row));
  }

  async createSnooze(input: SnoozeInput): Promise<NotificationSnooze> {
    const raw = await this.fetchJSON('/v1/notificationSnoozes', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return notificationSnoozeSchema.parse(raw);
  }

  async listSnoozes(includeExpired = false): Promise<NotificationSnooze[]> {
    const raw = await this.fetchJSON(
      `/v1/notificationSnoozes${includeExpired ? '?includeExpired=true' : ''}`,
    );
    const rows = Array.isArray(raw.snoozes) ? raw.snoozes : [];
    return rows.map((row) => notificationSnoozeSchema.parse(row));
  }

  async deleteSnooze(id: string): Promise<void> {
    await this.fetchJSON(`/v1/notificationSnoozes/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listPenActivity(month?: string): Promise<PenActivityMonth> {
    const raw = await this.fetchJSON(
      `/v1/penActivity${month ? `?month=${encodeURIComponent(month)}` : ''}`,
    );
    const rows = Array.isArray(raw.activity) ? raw.activity : [];
    return {
      month: typeof raw.month === 'string' ? raw.month : (month ?? ''),
      throughDay: typeof raw.throughDay === 'string' ? raw.throughDay : undefined,
      activity: rows
        .map((row) => penActivitySchema.parse(row))
        .filter((row) => row.deviceId && row.siteId),
    };
  }
}

export const notificationsApi = new LiveNotificationsApi();
